// src/components/categories/CategoryDragList.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Button,
  Alert,
  Snackbar
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
// Par celle-ci :
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { motion } from 'framer-motion';
import { useCategories } from '../../context/CategoryContext';
import { useTranslation } from 'react-i18next';
const CategoryDragList = () => {
  const { categoriesList, reorderCategories } = useCategories();
  const [items, setItems] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  // Initialiser les items avec les catégories
  useEffect(() => {
    if (categoriesList && categoriesList.length > 0) {
      // Trier par ordre d'affichage
      const sortedCategories = [...categoriesList].sort((a, b) => {
        // D'abord trier par parentId (null en premier)
        if (a.parentId === null && b.parentId !== null) return -1;
        if (a.parentId !== null && b.parentId === null) return 1;
        if (a.parentId !== b.parentId) return a.parentId?.localeCompare(b.parentId) || 0;
        
        // Ensuite par displayOrder
        return a.displayOrder - b.displayOrder;
      });
      
      setItems(sortedCategories);
    }
  }, [categoriesList]);

  // Gérer le drag and drop
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const reorderedItems = Array.from(items);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);
    
    // Mettre à jour les displayOrder
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      displayOrder: index
    }));
    
    setItems(updatedItems);
    setHasChanges(true);
  };

  // Sauvegarder l'ordre
  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Préparer les données pour l'API
      const categoriesOrder = items.map(item => ({
        id: item.id,
        displayOrder: item.displayOrder
      }));
      
      await reorderCategories(categoriesOrder);
      
      setHasChanges(false);
      setNotification({
        open: true,
        message: 'Ordre des catégories sauvegardé avec succès',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving category order:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de la sauvegarde de l\'ordre des catégories',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Réinitialiser l'ordre
  const handleReset = () => {
    if (categoriesList) {
      const sortedCategories = [...categoriesList].sort((a, b) => {
        if (a.parentId === null && b.parentId !== null) return -1;
        if (a.parentId !== null && b.parentId === null) return 1;
        if (a.parentId !== b.parentId) return a.parentId?.localeCompare(b.parentId) || 0;
        return a.displayOrder - b.displayOrder;
      });
      
      setItems(sortedCategories);
      setHasChanges(false);
    }
  };

  // Fermer la notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Obtenir le nom de la catégorie parente
  const getParentName = (parentId) => {
    if (!parentId) return null;
    const parent = categoriesList.find(cat => cat.id === parentId);
    return parent ? parent.name : 'Inconnu';
  };

  return (
    <Paper 
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          elevation={3} 
          sx={{ p: 3, borderRadius: 2 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">{t('categoryManagement.tabs.reorganize')}</Typography>
            <Box>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                disabled={!hasChanges || saving}
                sx={{ mr: 1 }}
              >
                {t('common.reset')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                {t('common.save')}
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('categoryManagement.ownerInfoMessage')}
          </Typography>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="categories">
              {(provided) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{ 
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: '60vh',
                    overflow: 'auto'
                  }}
                >
                  {items.map((item, index) => (
                    <Draggable key={item.id} draggableId={item.id} index={index}>
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                            borderLeft: '3px solid',
                            borderLeftColor: item.parentId ? 'secondary.main' : 'primary.main',
                            mb: 0.5,
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <ListItemIcon {...provided.dragHandleProps}>
                            <DragIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={item.name}
                            secondary={item.parentId ? t('categoryManagement.parent', { parent: getParentName(item.parentId) }) : t('categoryManagement.rootCategory')}
                          />
                          <ListItemSecondaryAction>
                            <Typography variant="body2" color="text.secondary">
                              {t('common.order', { order: item.displayOrder })}
                            </Typography>
                          </ListItemSecondaryAction>
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
          {hasChanges && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('categoryManagement.unsavedChanges')}
            </Alert>
          )}
          <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert onClose={handleCloseNotification} severity={notification.severity}>
              {notification.message}
            </Alert>
          </Snackbar>
        </Paper>
  );
};

export default CategoryDragList;